import { NgModule, Optional } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import {Router, RouterModule, Routes} from "@angular/router";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './auth-config';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
  MSAL_BROADCAST_CONFIG,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastConfiguration
} from '@azure/msal-angular';
import {SharedModule} from "./modules/shared/shared.module";
import {ReactiveFormsModule} from "@angular/forms";
import { HttpHeaderInterceptor } from './services/httpinterceptor';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HttpHelperService } from './services/http-helper.service';
import { AuthService } from './services/auth.service';
import {ServiceWorkerModule} from "@angular/service-worker";
import {environment} from "../environments/environment";
import {ModalModule} from "ngx-bootstrap/modal";
import {BsDatepickerModule} from "ngx-bootstrap/datepicker";
import { MinesideRedirectComponent } from './modules/default/components/mineside-redirect/mineside-redirect.component';
import {NgxSpinnerModule} from "ngx-spinner";
import { LogoutComponent } from './components/logout/logout.component';
import { AddCardOkComponent } from './modules/manage-card/add-card-ok/add-card-ok.component';
import { AddCardErrorComponent } from './modules/manage-card/add-card-error/add-card-error.component';
import { AddCardVerificationErrorComponent } from './modules/manage-card/add-card-verification-error/add-card-verification-error.component';
import { HomeComponent } from './modules/home/home.component';
import { VerifyCardStatusComponent } from './modules/manage-card/verify-card-status/verify-card-status.component';
import { Location } from '@angular/common';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
 export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
* Set your default interaction type for MSALGuard here. If you have any
* additional scopes you want the user to consent upon login, add them here as well.
*/
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
      interactionType: InteractionType.Redirect,
  };
}

/** Explicit factories so Ivy resolves MSAL deps (legacy msal-angular ctor metadata fails on Angular 16+). */
export function msalServiceFactory(instance: IPublicClientApplication, location: Location): MsalService {
  return new MsalService(instance, location);
}

export function msalBroadcastServiceFactory(
  instance: IPublicClientApplication,
  authService: MsalService,
  broadcastConfig: MsalBroadcastConfiguration | null
): MsalBroadcastService {
  return new MsalBroadcastService(instance, authService, broadcastConfig ?? undefined);
}

export function msalGuardFactory(
  guardConfig: MsalGuardConfiguration,
  broadcast: MsalBroadcastService,
  authService: MsalService,
  location: Location,
  router: Router
): MsalGuard {
  return new MsalGuard(guardConfig, broadcast, authService, location, router);
}

const routes: Routes = [
    { path: '', component: MinesideRedirectComponent, canActivate: [ MsalGuard ] },
    { path: 'manage-card/add-card-ok', component: AddCardOkComponent},
    { path: 'manage-card/add-card-error', component: AddCardErrorComponent},
    { path: 'manage-card/add-card-verification-error', component: AddCardVerificationErrorComponent},
    { path: 'manage-card/mittmedlemskap/bonus', component: VerifyCardStatusComponent},
    { path: 'auth/logout', component: LogoutComponent},
    { path: 'home', component: HomeComponent},
    { path: 'bankid', loadChildren: () => import('./modules/bankid/bankid.module').then(m => m.BankidModule), canActivate: [ MsalGuard ] },

    { path: 'account', loadChildren: () => import('./modules/account/account.module').then(m => m.AccountModule), canActivate: [ MsalGuard ] },
    { path: '**', redirectTo: 'home' },
];

@NgModule({ declarations: [
        AppComponent,
        MinesideRedirectComponent,
        LogoutComponent,
        AddCardOkComponent,
        AddCardErrorComponent,
        AddCardVerificationErrorComponent,
        HomeComponent,
        VerifyCardStatusComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(routes),
        SharedModule,
        ReactiveFormsModule,
        ModalModule.forRoot(),
        BsDatepickerModule.forRoot(),
        NgxSpinnerModule,
        // Registered once the app has been stable for 30 s, so the worker never
        // competes with the login chain for bandwidth on a first visit.
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:30000'
        })], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: HttpHeaderInterceptor, multi: true },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        { provide: MsalService, useFactory: msalServiceFactory, deps: [MSAL_INSTANCE, Location] },
        {
            provide: MsalBroadcastService,
            useFactory: msalBroadcastServiceFactory,
            deps: [MSAL_INSTANCE, MsalService, [new Optional(), MSAL_BROADCAST_CONFIG]],
        },
        {
            provide: MsalGuard,
            useFactory: msalGuardFactory,
            deps: [MSAL_GUARD_CONFIG, MsalBroadcastService, MsalService, Location, Router],
        },
        AuthService,
        HttpHelperService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
